// "All manuals aboard" — the first-class, searchable manuals library that did
// not exist before. Flattens every device's manuals (de-duped by id, since a
// manual is shared across all devices of a model) into one list with the owning
// device's name + presence, revision / language / size up front, and a direct
// Open. Supports ?q= (search) and ?device= (scope to one device) deep links.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useBoatChrome } from '../components/app/boatChrome';
import { DocIcon, SearchIcon } from '../components/app/icons';
import { presenceDotClass } from '../components/app/boatChrome';
import { formatBytes, seenStatus } from '../lib/deviceSeen';
import type { UserManual, VesselDevice } from '../lib/useBoatDevices';
import { useDocumentTitle } from '../lib/useDocumentTitle';

interface FlatManual {
  manual: UserManual;
  device: VesselDevice;
}

type SortKey = 'device' | 'manufacturer' | 'size';

function flattenManuals(devices: VesselDevice[], deviceFilter: string | null): FlatManual[] {
  const seen = new Set<string>();
  const out: FlatManual[] = [];
  for (const d of devices) {
    if (deviceFilter !== null && d.deviceId !== deviceFilter) continue;
    for (const m of d.manuals ?? []) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      out.push({ manual: m, device: d });
    }
  }
  return out;
}

export function Manuals() {
  const { boat, devices } = useBoatChrome();
  useDocumentTitle(boat ? `${boat.display_name} — Manuals` : 'Manuals — Neomaris');

  const [params, setParams] = useSearchParams();
  const deviceFilter = params.get('device');
  const [query, setQuery] = useState(() => params.get('q') ?? '');
  const [sort, setSort] = useState<SortKey>('device');
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // keep ?q= in the URL so the view is linkable/bookmarkable
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (query) next.set('q', query);
    else next.delete('q');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // Keyed on the typed query alone, deliberately: this mirrors state INTO the
    // URL, it is not a two-way reconcile. `params` changes identity on every
    // location change and `setParams` is a useCallback over it, so listing
    // either would re-run this on any navigation and re-assert the in-component
    // `query` over whatever the URL had just become — and since `query` is
    // seeded from ?q= only once, at mount, that would quietly defeat the back
    // button and any inbound link that carried a different ?q=.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // The ready/not-ready branch lives inside the memo on purpose. Hoisted into a
  // `const inventory = ... : []` above it, the not-ready branch minted a fresh
  // empty array on every render, so the dependency it fed changed identity every
  // render and the memo never actually memoized (react-hooks/exhaustive-deps
  // flags exactly this). `devices.state` is the inventory hook's useState value,
  // so its identity changes only when that hook re-resolves — which is the real
  // trigger for recomputing the flattened list.
  const all = useMemo(
    () => flattenManuals(devices.state.kind === 'ready' ? devices.state.devices : [], deviceFilter),
    [devices.state, deviceFilter]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q
      ? all.filter(({ manual, device }) =>
          `${manual.title} ${manual.revision ?? ''} ${manual.language ?? ''} ${device.displayName} ${
            device.manufacturer?.name ?? ''
          }`
            .toLowerCase()
            .includes(q)
        )
      : all.slice();
    rows.sort((a, b) => {
      if (sort === 'manufacturer') {
        return (a.device.manufacturer?.name ?? '').localeCompare(b.device.manufacturer?.name ?? '');
      }
      if (sort === 'size') {
        return (b.manual.sizeBytes ?? 0) - (a.manual.sizeBytes ?? 0);
      }
      return a.device.displayName.localeCompare(b.device.displayName);
    });
    return rows;
  }, [all, query, sort]);

  const totalBytes = all.reduce((sum, { manual }) => sum + (manual.sizeBytes ?? 0), 0);

  return (
    <section data-testid="manuals">
      <div className="lib-head">
        <div className="page-head" style={{ margin: 0 }}>
          <h1>All manuals aboard</h1>
          <p className="lib-stats">
            <strong>
              {all.length} manual{all.length === 1 ? '' : 's'}
            </strong>
            {totalBytes > 0 ? (
              <>
                {' · '}
                <strong>{formatBytes(totalBytes)}</strong>
              </>
            ) : null}
            {' · '}
            matched to the devices{' '}
            {deviceFilter ? 'on this device' : `on ${boat?.display_name ?? 'this boat'}`}
          </p>
        </div>
      </div>

      {all.length > 0 ? (
        <div className="lib-controls">
          <div className="search-box">
            <SearchIcon className="search-ico" />
            <input
              type="search"
              value={query}
              placeholder="Search manuals aboard…"
              autoComplete="off"
              aria-label="Search manuals aboard"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <select
            className="sort-select"
            aria-label="Sort manuals"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
          >
            <option value="device">Sort by device</option>
            <option value="manufacturer">Sort by manufacturer</option>
            <option value="size">Sort by size</option>
          </select>
        </div>
      ) : null}

      <div className="manual-list" data-testid="manuals-library">
        {devices.state.kind === 'loading' ? <p className="muted">Loading manuals…</p> : null}
        {all.length === 0 && devices.state.kind === 'ready' ? (
          <div className="empty-card">
            <p>No device manuals yet.</p>
            <p className="muted">
              Manuals appear here once they're published for the device models aboard. Availability
              does not depend on a device being online.
            </p>
          </div>
        ) : null}
        {filtered.map(({ manual: m, device: d }) => {
          const seen = seenStatus(d.lastSeen, now);
          const size = m.sizeBytes != null ? formatBytes(m.sizeBytes) : '';
          const spec = [m.revision, m.language, size].filter(Boolean).join(' · ');
          return (
            <div className="manual-row" data-testid={`manual-row-${m.id}`} key={m.id}>
              <div className="mr-doc">
                <DocIcon />
              </div>
              <div className="mr-main">
                <div className="mr-title">{m.title}</div>
                <div className="mr-device">
                  <span
                    className={`boat-status-dot boat-status-dot--${presenceDotClass(seen.state)}`}
                    style={{ width: 7, height: 7 }}
                    title={seen.status}
                  />{' '}
                  {d.preferredName ?? d.displayName}
                </div>
                {spec ? <div className="mr-spec">{spec}</div> : null}
              </div>
              <div className="mr-actions">
                <a
                  className="btn-open-sm"
                  href={m.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`open-manual-row-${m.id}`}
                >
                  Open
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
